import { FormErrors, FormValueInput, FormValueMeta } from 'types'
import { ControllerFieldState, ControllerRenderProps, Path, PathValue } from 'react-hook-form'

/**
 * Get the final part of the path of the given field name in a form, useful for setting data-cy for tests
 *
 * @param name the form field name to shorten
 * @returns a shorter name, with only the part after the final dot
 */
export function getShortName(name?: string): string | undefined {
  if (!name) return name
  const parts = name.split('.')
  return parts[parts.length - 1]
}

export interface FormValidationResult<FormFields> {
  values: FormFields | {}
  errors: FormErrors<FormFields>
}

/**
 * Convert form errors to a structure that can be returned by a react-hook-form resolver
 *
 * @param values the values being validated
 * @param formErrors the errors resulting from the validation
 */
export function formValidationResult<FormFields>(values: FormFields, formErrors: FormErrors<FormFields>): FormValidationResult<FormFields> {
  const result: FormValidationResult<FormFields> = {
    values: Object.keys(formErrors || {}).length === 0 ? values : {},
    errors: formErrors,
  }
  return result
}

/**
 * Convert react-hook-form field data to a FormValueInput object
 *
 * @param field the field data to convert
 */
export function formFieldToInput<FormValues, TName extends Path<FormValues>>(
  field: ControllerRenderProps<FormValues, TName>,
): FormValueInput<PathValue<FormValues, TName>> {
  if (!field) return null

  return {
    value: field.value,
    name: field.name,
    onBlur: field.onBlur,
    onChange: field.onChange,
  }
}

/**
 * Convert react-hook-form fieldState data to a FormValueMeta object
 *
 * @param fieldState the fieldState data to convert
 */
export function fieldStateToMeta(fieldState: ControllerFieldState & { error?: { _error?: string } }): FormValueMeta {
  if (!fieldState) return null

  const error = typeof fieldState.error === 'string' ? fieldState.error : fieldState.error?.message || fieldState.error?.type || fieldState.error?._error

  return {
    error: error,
    touched: fieldState.isTouched || Boolean(error), // TODO: double check if this is correct
  }
}

export function validateEmail(email: string) {
  if (!email) return false

  // Updated regex to support:
  // - TLDs from 2 to 63 characters (RFC standard, though most are much shorter)
  // - Multiple dots in domain (subdomains)
  // - Modern long TLDs like .international (13 chars), .consulting (10 chars), etc.
  return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,63}$/i.test(email)
}
